import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import Logo from "@/components/Logo";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [status, setStatus] = useState("checking"); // checking | paid | expired | failed

    useEffect(() => {
        if (!sessionId) {
            setStatus("failed");
            return;
        }
        let attempts = 0;
        const maxAttempts = 8;
        let cancelled = false;
        const poll = async () => {
            attempts += 1;
            try {
                const { data } = await api.get(`/checkout/status/${sessionId}`);
                if (cancelled) return;
                if (data.payment_status === "paid") {
                    setStatus("paid");
                    await refresh();
                    return;
                }
                if (data.status === "expired") {
                    setStatus("expired");
                    return;
                }
            } catch (_e) {
                /* keep polling */
            }
            if (attempts < maxAttempts && !cancelled) {
                setTimeout(poll, 2000);
            } else if (!cancelled) {
                setStatus("failed");
            }
        };
        poll();
        return () => {
            cancelled = true;
        };
    }, [sessionId, refresh]);

    return (
        <div className="min-h-screen bg-zinc-950 grain flex items-center justify-center px-6">
            <div className="absolute top-6 left-6">
                <Logo size="md" />
            </div>
            <div className="max-w-md w-full glass-card rounded-3xl p-10 text-center" data-testid="payment-status-card">
                {status === "checking" && (
                    <>
                        <Loader2 className="w-12 h-12 animate-spin text-orange-400 mx-auto mb-5" />
                        <h1 className="font-heading text-2xl font-bold mb-2">Confirming your payment...</h1>
                        <p className="text-zinc-400 text-sm">Hang tight, this takes a few seconds.</p>
                    </>
                )}
                {status === "paid" && (
                    <>
                        <CheckCircle2 className="w-14 h-14 text-orange-400 mx-auto mb-5" />
                        <h1 className="font-heading text-3xl font-bold mb-2">You're in!</h1>
                        <p className="text-zinc-400 mb-7">Your plan is active. Fresh credits have been added to your account.</p>
                        <button
                            type="button"
                            onClick={() => navigate("/dashboard")}
                            data-testid="payment-go-dashboard-btn"
                            className="btn-magic w-full py-3 rounded-full text-white font-semibold"
                        >
                            Go to dashboard
                        </button>
                    </>
                )}
                {(status === "expired" || status === "failed") && (
                    <>
                        <AlertCircle className="w-12 h-12 text-rose-400 mx-auto mb-5" />
                        <h1 className="font-heading text-2xl font-bold mb-2">Couldn't confirm payment</h1>
                        <p className="text-zinc-400 mb-7">Please try again or contact support.</p>
                        <Link to="/pricing" className="btn-magic inline-block py-3 px-6 rounded-full text-white font-semibold">
                            Back to pricing
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
